<script setup lang="ts">
import { defineProps, defineEmits } from 'vue';
import type { ArticleV1 } from '@ilteducation/content-registry-types';
import { useDisplay } from 'vuetify';

type Props = {
  relatedArticle: ArticleV1;
};

const props = defineProps<Props>();

const { smAndDown } = useDisplay();

const emit = defineEmits<{
  (e: 'openArticle', id: string): void;
}>();

const onOpen = (id: string) => emit('openArticle', id);

const getPublisherInfo = (article: ArticleV1) => {
  const { publisher, publicationTimestamp } = article;
  if (publicationTimestamp) {
    return `${publisher}, ${new Date(publicationTimestamp).getFullYear()}`;
  }
  return publisher;
};
</script>
<template>
  <a
    href="#"
    class="link-to-article"
    @click.prevent="onOpen(props.relatedArticle.id)"
    @keyup.enter.prevent="onOpen(props.relatedArticle.id)"
    @keyup.space.prevent="onOpen(props.relatedArticle.id)"
  >
    <v-card flat>
      <v-row align="center">
        <v-col cols="3" class="mb-3 mt-3 pa-sm-3">
          <v-img :src="props.relatedArticle.cover?.url" cover class="responsive-img" alt=""></v-img>
        </v-col>
        <v-col cols="9">
          <v-card-title
            class="text-subtitle-1 font-weight-bold pl-0"
            :class="{
              'multi-line-truncate': smAndDown,
              'text-truncate': !smAndDown,
            }"
            >{{ props.relatedArticle.headline }}
          </v-card-title>
          <v-card-text v-if="!smAndDown" class="multi-line-truncate font-weight-medium pl-0"
            >{{ props.relatedArticle.description }}
          </v-card-text>
          <v-card-text class="multi-line-truncate font-italic pl-0" :class="{ smAndDown: 'pt-0' }">
            <div class="publisher-info on-surface-medium float-md-right">
              Tidskrift: {{ getPublisherInfo(props.relatedArticle) }}
            </div>
          </v-card-text>
        </v-col>
      </v-row>
    </v-card>
  </a>
</template>

<style lang="scss" scoped>
.multi-line-truncate {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  height: calc(1.2em * 2);
  line-height: 1.2em;
}

.publisher-info {
  font-size: 0.875rem;
}

.responsive-img {
  width: 100%;
  height: auto;
  object-fit: cover;
}

@media (max-width: 600px) {
  .responsive-img {
    width: 100%;
    height: 100%;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    object-position: center;
  }
}

.link-to-article {
  text-decoration: inherit;
  color: inherit;
}
</style>
